import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, withCtx as _withCtx, createVNode as _createVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-3d5e157a"),n=n(),_popScopeId(),n)
const _hoisted_1 = { id: "gmap" }

import http from '@/js/utils/http.js'
import API from '@/js/const/api'
import APP from '@/js/const/app'
import i18n from '@/plugins/i18n'
import { zeroPadding } from '@/js/utils/filters'
import { GoogleMap, Marker, Polygon } from 'vue3-google-map'
import { useStore } from 'vuex'
import { computed, onMounted, ref, watch, withDefaults } from 'vue'
import {
  Camera,
  CameraByLocationArea,
  CameraByLocationCity,
  CameraByLocationRange,
} from '@/@types/typescript-axios'
import axios, { AxiosResponse } from 'axios'
import { ElMessage } from 'element-plus'

type LatLngType = {
  lat: number
  lng: number
}
type PathsType = {
  path: LatLngType[]
}
type FeatureType = {
  geometry: { coordinates: number[][] | number[]; type: string }
  properties: {
    'JIS-CODE': number
    ObjName: string
    ObjName_1: string
  }
  type: string
}
type MarkerType = {
  clickable: boolean
  code: number
  label: { text: string; color: string; fontWeight: string; fontFamily: string }
  markerIcon: { url: string; origin: string }
  position: { lat: number; lng: number }
}

type PropsType = {
  cameraLatLon: number[]
  firstCamera: Camera
  weatherApiInfo: (string | boolean | Date | null)[]
  backMarker?: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'MapView',
  props: {
    cameraLatLon: null,
    firstCamera: null,
    weatherApiInfo: null,
    backMarker: { type: Boolean, default: false }
  },
  emits: ["hideLocationCameraList", "locationCameraList", "backMarkerStateChange"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'hideLocationCameraList'): void
  (e: 'locationCameraList', locationCode: number): void
  (e: 'backMarkerStateChange'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { cameraLatLon: number[], firstCamera: Camera, weatherApiInfo: (string | boolean | Date | null)[], backMarker: boolean };

const store = useStore()

// 皇宮の座標
const BASE_LAT_LON = { lat: 35.6852797, lng: 139.7516308 }
const RED_COLOR = '#ff0000'
const BLUE_COLOR = '#0000ff'
const YELLOW_COLOR = '#FFFF00'
const PURPLE_COLOR = '#800080'
const GREEN_COLOR = '#008800'
const RED_ICON_URL = '//maps.google.com/mapfiles/ms/icons/red-dot.png'
const GREEN_ICON_URL = '//maps.google.com/mapfiles/ms/icons/green-dot.png'
const mapType = APP.MAP_TYPE

const googleMap = ref<HTMLElement>()
const markers = ref<MarkerType[]>([])
const zoom = ref<number>(16)
const mapZoom = ref<number>(16)
const cityData = ref<CameraByLocationCity[]>([])
const prefData = ref<CameraByLocationArea[]>([])
const rangeCameraData = ref<CameraByLocationRange[]>([])
const totalCount = ref<number>(0)
const weatherMessageState = ref<boolean>(true)
const mapLat = ref<number>(BASE_LAT_LON.lat)
const mapLng = ref<number>(BASE_LAT_LON.lng)
const selectMarker = ref<MarkerType | null>(null)
const advisoryPaths = ref<PathsType[]>([])
const emergencyPaths = ref<PathsType[]>([])
const warningPaths = ref<PathsType[]>([])
const isMapChange = ref<boolean>(false)





const settings = computed(() => store.getters['app/settings'])
const mapStyle = {
  width: '100%',
  height: '100%',
}

onMounted(() => {
  initialize()
})

const initialize = async () => {
  // リストで初めてのカメラに問題がある場合
  if (
    !props.firstCamera ||
    props.firstCamera.location_latitude > 90 ||
    props.firstCamera.location_longitude > 180
  ) {
    mapLat.value = BASE_LAT_LON.lat
    mapLng.value = BASE_LAT_LON.lng
    // geolocationUse = true
  } else {
    mapLat.value = props.firstCamera.location_latitude
    mapLng.value = props.firstCamera.location_longitude
  }
  // カメラや地役のデータ
  await getCameraDataByCity()
  await getCameraDataByArea()
}

const isZoomCountry = (zoom: number) => {
  return zoom <= 5
}

const isZoomPref = (zoom: number) => {
  return zoom > 5 && zoom <= 7
}

const isZoomCity = (zoom: number) => {
  return zoom > 7 && zoom <= 12
}

const isZoomLocation = (zoom: number) => {
  return zoom > 12
}

const getCameraDataByCity = () => {
  return http.get(API.CAMERAS_LOCATION_CITY).then((res: AxiosResponse) => {
    cityData.value = res.data.data
  })
}

//  カメラのマーカ
const addLocationMarkers = () => {
  const bounds = googleMap.value?.map.getBounds()
  const sw = bounds.getSouthWest()
  const ne = bounds.getNorthEast()
  getCameraDataByRange(sw.lat(), ne.lat(), sw.lng(), ne.lng())
}

//  都市のマーカ
const addCityMarkers = () => {
  markers.value = []
  cityData.value.forEach((one) => {
    const text = one.city_jp + ': ' + one.camera_count
    const position = {
      position: { lat: Number(one.latitude), lng: Number(one.longitude) },
      label: {
        text: text,
        color: BLUE_COLOR,
        fontWeight: 'bold',
        fontFamily: 'Courier New, monospace',
      },
      markerIcon: {
        url: GREEN_ICON_URL,
        origin: new googleMap.value.api.Point(100, 0),
      },
      clickable: false,
    }
    markers.value.push(position)
  })
  isMapChange.value = false
}

//  県のマーカ
const addPrefMarkers = () => {
  markers.value = []
  for (const i in prefData.value) {
    const one = prefData.value[i]
    let area = one.area_jp
    if (area !== '北海道' && area) {
      area = area.substring(0, area.length - 1)
    }
    const count = one.camera_count
    const text = area + ': ' + count
    const position = {
      position: { lat: Number(one.latitude), lng: Number(one.longitude) },
      label: {
        text: text,
        color: RED_COLOR,
        fontWeight: 'bold',
        fontFamily: 'Courier New, monospace',
      },
      markerIcon: {
        url: GREEN_ICON_URL,
        origin: new googleMap.value.api.Point(100, 0),
      },
      clickable: false,
    }
    markers.value.push(position)
  }
  isMapChange.value = false
}

//  国のマーカ
const addCountryMarker = () => {
  markers.value = []
  const lat = 35.686198
  const lng = 139.753211
  const text = i18n.global.t('map_view.japan') + totalCount.value
  const position = {
    position: { lat: lat, lng: lng },
    label: {
      text: text,
      color: RED_COLOR,
      fontWeight: 'bold',
      fontFamily: 'Courier New, monospace',
    },
    markerIcon: {
      url: GREEN_ICON_URL,
      origin: new googleMap.value.api.Point(100, 0),
    },
    clickable: false,
  }
  markers.value.push(position)
  isMapChange.value = false
}

//  カメラデータ
const getCameraDataByRange = (latMin: number, latMax: number, lngMin: number, lngMax: number) => {
  const cameraRangeApi = API.CAMERAS_LOCATION_RANGE
  const params = {
    lat_min: latMin,
    lat_max: latMax,
    lng_min: lngMin,
    lng_max: lngMax,
  }

  return http.get(cameraRangeApi, params).then((res: AxiosResponse) => {
    if (!isZoomLocation(zoom.value)) {
      return
    }
    rangeCameraData.value = res.data.data
    markers.value = []
    for (const i in rangeCameraData.value) {
      const one = rangeCameraData.value[i]
      const position = {
        position: { lat: one.latitude, lng: one.longitude },
        label: {
          text: one.name,
          color: GREEN_COLOR,
          fontWeight: 'bold',
          fontFamily: 'Courier New, monospace',
        },
        code: one.code,
        markerIcon: {
          url: GREEN_ICON_URL,
          labelOrigin: new googleMap.value.api.Point(15, 40),
        },
        clickable: true,
      }
      markers.value.push(position)
    }
    if (selectMarker.value) {
      markers.value.push(selectMarker.value)
    }
    isMapChange.value = false
  })
}

const getCameraDataByArea = () => {
  return http.get(API.CAMERAS_LOCATION_AREA).then((res: AxiosResponse) => {
    prefData.value = res.data.data
    for (const i in prefData.value) {
      const count = prefData.value[i].camera_count
      totalCount.value += count
    }
  })
}

const change = () => {
  if (isMapChange.value) {
    return
  }
  isMapChange.value = true
  onMarkers()
}

//  状態によってマーカ修正
const onMarkers = () => {
  const map = googleMap.value?.map
  if (isZoomLocation(map.zoom)) {
    addLocationMarkers()
  } else if (isZoomCity(map.zoom)) {
    if (isZoomLocation(mapZoom.value)) {
      if (selectMarker.value) {
        emit('hideLocationCameraList')
      }
    }
    addCityMarkers()
  } else if (isZoomPref(map.zoom)) {
    addPrefMarkers()
  } else if (isZoomCountry(map.zoom)) {
    addCountryMarker()
  }
  mapZoom.value = map.zoom
}

// カメラリスト開く
const toggleMarker = (marker: MarkerType) => {
  if (isMapChange.value || !marker.clickable) {
    return
  }
  if (marker.code) {
    emit('locationCameraList', marker.code)
  }
  if (marker.markerIcon) {
    if (marker.markerIcon.url === GREEN_ICON_URL) {
      marker.markerIcon = {
        url: RED_ICON_URL,
        labelOrigin: new googleMap.value.api.Point(15, 40),
      }
      marker.label = {
        text: marker.label.text,
        color: RED_COLOR,
        fontWeight: 'bold',
        fontFamily: 'Courier New, monospace',
      }
      // 前の選択マーカーを戻す
      if (selectMarker.value) {
        greenMarker()
      }
      selectMarker.value = marker
    }
  }
}

const greenMarker = () => {
  if (selectMarker.value) {
    selectMarker.value.markerIcon = {
      url: GREEN_ICON_URL,
      labelOrigin: new googleMap.value.api.Point(15, 40),
    }
    selectMarker.value.label = {
      text: selectMarker.value.label.text,
      color: GREEN_COLOR,
      fontWeight: 'bold',
      fontFamily: 'Courier New, monospace',
    }
  }
}

// レイヤ情報や適用
const getWeatherInfo = (date: Date, time: string, type: string) => {
  const paramDate =
    String(date.getFullYear()) +
    zeroPadding(date.getMonth() + 1, 2) +
    zeroPadding(date.getDate(), 2)
  const paramTime = zeroPadding(time, 2)
  const weatherApi = API.WEATHER_INFO.replace(
    '{date}/{time}/{type}',
    paramDate + '/' + paramTime + '/' + type
  )
  http
    .get(weatherApi)
    .then((res: AxiosResponse) => {
      const paths = getPaths(res.data.data.features)
      if (type.startsWith('advisory')) {
        advisoryPaths.value = paths
      } else if (type.startsWith('emergency_warning')) {
        emergencyPaths.value = paths
      } else if (type.startsWith('warning')) {
        warningPaths.value = paths
      }
    })
    .catch((error: unknown) => {
      if (!axios.isAxiosError(error)) throw error
      //  メッセージの重複防止
      if (weatherMessageState.value) {
        if (error.response?.status === 404) {
          ElMessage({
            message: i18n.global.t('map_view.select_date'),
            type: 'warning',
          })
        } else {
          ElMessage({
            message: i18n.global.t('map_view.communication_failed'),
            type: 'error',
          })
        }
        weatherMessageState.value = false
      }
    })
}

/**
 * WKT 形式のポリゴンデータを GmapPolygon の paths に変換
 */
const getPaths = (features: FeatureType[]) => {
  const paths = []
  for (const i in features) {
    const geometry = features[i].geometry
    if (geometry.type === 'Polygon') {
      const area = geometry.coordinates[0].map((p: number[]) => {
        return { lat: parseFloat(p[1]), lng: parseFloat(p[0]) }
      })
      paths.push({ path: area })
    } else if (geometry.type === 'MultiPolygon') {
      const polygons = geometry.coordinates
      for (const j in polygons) {
        const area = polygons[j][0].map((p: number[]) => {
          return { lat: parseFloat(p[1]), lng: parseFloat(p[0]) }
        })
        paths.push({ path: area })
      }
    }
  }
  return paths
}

const setWeatherLayer = (
  date: Date | null,
  time: string | null,
  weather: string | null,
  typeNumber: number | null
) => {
  advisoryPaths.value = []
  emergencyPaths.value = []
  warningPaths.value = []
  if (typeNumber === 0) {
    return
  }
  if (!weather && date && time) {
    getWeatherInfo(date, time, 'advisory')
    getWeatherInfo(date, time, 'emergency_warning')
    getWeatherInfo(date, time, 'warning')
  } else {
    if (weather && date && time) {
      getWeatherInfo(date, time, weather)
    }
  }
}

const getMarkerOptions = (marker: MarkerType) => {
  return {
    position: marker.position,
    clickable: marker.clickable,
    draggable: false,
    icon: marker.markerIcon,
    label: marker.label,
  }
}

const getAdvisoryPolygonOptions = (data: PathsType) => {
  return {
    paths: data.path,
    draggable: false,
    strokeColor: YELLOW_COLOR,
    fillColor: YELLOW_COLOR,
    fillOpacity: '0.3',
    strokeWeight: 2,
  }
}

const getEmergencyPolygonOptions = (data: PathsType) => {
  return {
    paths: data.path,
    draggable: false,
    strokeColor: PURPLE_COLOR,
    fillColor: PURPLE_COLOR,
    fillOpacity: '0.3',
    strokeWeight: 2,
  }
}

const getWarningPolygonOptions = (data: PathsType) => {
  return {
    paths: data.path,
    draggable: false,
    strokeColor: RED_COLOR,
    fillColor: RED_COLOR,
    fillOpacity: '0.3',
    strokeWeight: 2,
  }
}

watch(
  () => props.cameraLatLon,
  () => {
    if (!isZoomLocation(googleMap.value?.map.zoom)) {
      mapZoom.value = 16
      googleMap.value?.map.setZoom(mapZoom.value)
    }
    if (!props.cameraLatLon || props.cameraLatLon[0] > 90 || props.cameraLatLon[1] > 180) {
      mapLat.value = BASE_LAT_LON.lat
      mapLng.value = BASE_LAT_LON.lng
      ElMessage({
        message: i18n.global.t('map_view.problem_latlon'),
        type: 'error',
      })
    } else {
      mapLat.value = props.cameraLatLon[0]
      mapLng.value = props.cameraLatLon[1]
    }
  }
)

watch(
  () => props.weatherApiInfo,
  () => {
    if (
      props.weatherApiInfo[0] &&
      props.weatherApiInfo[1] &&
      props.weatherApiInfo[4] &&
      !props.weatherApiInfo[5]
    ) {
      setWeatherLayer(
        props.weatherApiInfo[0],
        props.weatherApiInfo[1],
        props.weatherApiInfo[2],
        props.weatherApiInfo[3]
      )
    }
  }
)

watch(
  () => props.backMarker,
  (v) => {
    if (v) {
      greenMarker()
      emit('backMarkerStateChange')
    }
  }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(GoogleMap), {
      ref_key: "googleMap",
      ref: googleMap,
      "api-key": _unref(settings).googleMapsApiKey,
      center: { lat: mapLat.value, lng: mapLng.value },
      zoom: zoom.value,
      style: mapStyle,
      "zoom-control": true,
      "street-View-control": false,
      styles: _unref(mapType),
      draggableCursor: "default",
      draggingCursor: "move",
      language: _ctx.$t('main_js.lang'),
      onIdle: change
    }, {
      default: _withCtx(() => [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(markers.value, (marker, index) => {
          return (_openBlock(), _createBlock(_unref(Marker), {
            key: index,
            options: getMarkerOptions(marker),
            onClick: ($event: any) => (toggleMarker(marker))
          }, null, 8, ["options", "onClick"]))
        }), 128)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(advisoryPaths.value, (advisoryData, index) => {
          return (_openBlock(), _createBlock(_unref(Polygon), {
            key: index + 'advisory',
            options: getAdvisoryPolygonOptions(advisoryData)
          }, null, 8, ["options"]))
        }), 128)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(emergencyPaths.value, (emergencyData, index) => {
          return (_openBlock(), _createBlock(_unref(Polygon), {
            key: index + 'emergency',
            options: getEmergencyPolygonOptions(emergencyData)
          }, null, 8, ["options"]))
        }), 128)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(warningPaths.value, (warningData, index) => {
          return (_openBlock(), _createBlock(_unref(Polygon), {
            key: index + 'warning',
            options: getWarningPolygonOptions(warningData)
          }, null, 8, ["options"]))
        }), 128))
      ]),
      _: 1
    }, 8, ["api-key", "center", "zoom", "styles", "language"])
  ]))
}
}

})